/**
 * Error recovery for the Flex app.
 *
 * A crash into the root error boundary strands a Flex user in a way it never
 * strands a web user: the webview has no back button and no address bar, so
 * there is no way to navigate out of the broken page by hand. Recovery is to
 * send them back to the last page that rendered successfully, or to the
 * dashboard when there isn't one (or when that page is the one that crashed).
 *
 * Guarded so a page that crashes every time can't become a redirect loop: one
 * automatic recovery per RECOVERY_GUARD_MS, after which the error screen stays
 * up with its manual actions. Same shape as the chunk-reload guard.
 */

/** Where a Flex user goes when there is no safe previous page: the dashboard. */
export const FLEX_HOME_PATH = "/flex/dashboard";

const LAST_PATH_KEY = "amber:flex-last-path";
const ATTEMPTS_KEY = "amber:flex-recovery-attempts";

// Crashes closer together than this are treated as the same incident, so the
// attempts escalate instead of restarting from the top. Further apart, the
// counter resets and the next crash gets a full set of attempts again.
const INCIDENT_WINDOW_MS = 60_000;

// Attempts per incident before we stop navigating and show the fallback
// screen. Two is the whole ladder: the last good page, then the dashboard on
// a fresh document. A page that survives neither is genuinely broken, and a
// third navigation would just be the start of a reload loop.
const MAX_ATTEMPTS = 2;

// Set the moment a recovery navigation is started, so a second call before
// the document actually unloads (React's dev double-invoke, a re-render of
// the error screen) reports "already recovering" instead of counting again.
let navigating = false;

function session(): Storage | null {
	try {
		return window.sessionStorage;
	} catch {
		// Private mode / blocked storage. Recovery still works, it just can't
		// remember the previous page or guard against a loop.
		return null;
	}
}

/**
 * Remember a page that rendered without throwing. Called on every successful
 * route render, so the value is always the last good page at crash time.
 */
export function recordFlexPath(pathname: string): void {
	if (!pathname) return;
	try {
		session()?.setItem(LAST_PATH_KEY, pathname);
	} catch {
		// Non-fatal: we fall back to the dashboard.
	}
}

/**
 * The page to return to after a crash on `currentPath` — the last good page,
 * or the dashboard when that is missing or is itself what crashed.
 */
export function getFlexRecoveryPath(currentPath: string): string {
	let lastPath: string | null = null;
	try {
		lastPath = session()?.getItem(LAST_PATH_KEY) ?? null;
	} catch {
		lastPath = null;
	}

	if (!lastPath || lastPath === currentPath) return FLEX_HOME_PATH;
	return lastPath;
}

/**
 * Full-document navigation. `replace` keeps the broken URL out of the history
 * stack. The target is fetched with `cache: "reload"` first, for the same
 * reason hardReload() in chunkReload.ts does it: index.html ships without
 * Cache-Control, so the browser may otherwise answer the navigation from its
 * cache with the very HTML — and the very dead chunk hashes — that just failed.
 */
function navigate(path: string): void {
	const url = `${window.location.origin}${path}`;
	const go = () => window.location.replace(url);
	try {
		fetch(url, { cache: "reload", credentials: "same-origin" })
			.then(go)
			// Offline or blocked — navigate anyway, it is still the best move.
			.catch(go);
	} catch {
		go();
	}
}

interface Attempts {
	count: number;
	ts: number;
}

function readAttempts(now: number): Attempts {
	try {
		const raw = session()?.getItem(ATTEMPTS_KEY);
		if (raw) {
			const parsed = JSON.parse(raw) as Partial<Attempts>;
			const count = Number(parsed.count) || 0;
			const ts = Number(parsed.ts) || 0;
			// Old incident: start over.
			if (now - ts < INCIDENT_WINDOW_MS) return { count, ts };
		}
	} catch {
		// Unreadable: treat as a first attempt.
	}
	return { count: 0, ts: now };
}

function writeAttempts(attempts: Attempts): void {
	try {
		session()?.setItem(ATTEMPTS_KEY, JSON.stringify(attempts));
	} catch {
		// Can't guard; the in-memory `navigating` flag still stops a tight loop.
	}
}

/**
 * Attempt an automatic recovery. Safe to call during render: it starts the
 * navigation synchronously, so the caller can show a loader on the very first
 * paint and the error screen never appears.
 *
 * Returns true when a navigation is (or already was) started, false when the
 * attempts for this incident are used up and the fallback screen should show.
 *
 * Escalates per incident:
 *   1. the last page that rendered successfully (or the dashboard)
 *   2. the dashboard, on a fresh document
 * A crash on the dashboard itself skips straight to a fresh document of it.
 */
export function recoverFlexError(currentPath: string): boolean {
	if (navigating) return true;

	const now = Date.now();
	const attempts = readAttempts(now);
	if (attempts.count >= MAX_ATTEMPTS) return false;

	const target =
		attempts.count === 0 ? getFlexRecoveryPath(currentPath) : FLEX_HOME_PATH;

	writeAttempts({ count: attempts.count + 1, ts: now });
	navigating = true;
	navigate(target);
	return true;
}

/** Manual "Restart": always go to the dashboard, bypassing the guard. */
export function goToFlexHome(): void {
	navigate(FLEX_HOME_PATH);
}
